// Document-slot registry — the FE counterpart to backend's §3 table.
// Slot ids are part of the API contract, so don't rename without
// coordinating with the server side.

import type { OT, OTDocSlotId, OTSlotDoc, OTStage } from "@/features/transit/api/types"

export interface OTDocSlot {
  id: OTDocSlotId
  stage: OTStage
  required: true | false | "fumigation"
}

export const OT_DOC_SLOTS_ALL: OTDocSlot[] = [
  // CREATED
  { id: "ordreDeTransit", stage: "CREATED", required: true },
  { id: "instructionEmbarquement", stage: "CREATED", required: true },
  { id: "proforma", stage: "CREATED", required: true },
  { id: "engagementEchange", stage: "CREATED", required: true },

  // ANALYSE — none (BV/BA live on the OT-Lot link)

  // FORMULE
  { id: "bookingConfirmation", stage: "FORMULE", required: true },
  { id: "formule", stage: "FORMULE", required: true },
  { id: "miseAQuai", stage: "FORMULE", required: true },
  { id: "formuleDefinitive", stage: "FORMULE", required: true },

  // EMPOTAGE — managed via `OT.empotageFiles[]` with `kind` discriminator
  // (rapportEmpotage / ticketPesee / other). No slot doc registry entries.

  // EMBARQUEMENT
  { id: "draftBL", stage: "EMBARQUEMENT", required: true },
  { id: "embarkmentConfirmation", stage: "EMBARQUEMENT", required: true },

  // REMISE
  { id: "blOriginal", stage: "REMISE", required: true },
  { id: "facture", stage: "REMISE", required: true },
  { id: "certificatPhytosanitaire", stage: "REMISE", required: true },
  { id: "attestationDesinfectionRemise", stage: "REMISE", required: true },
  { id: "listeColisage", stage: "REMISE", required: true },
  { id: "certificatOrigine", stage: "REMISE", required: true },
  { id: "courierTrackingDoc", stage: "REMISE", required: false },
]

// Slots that apply to a given OT — filters out the conditional fumigation
// slot when the OT has it disabled.
export function otDocSlots(ot: OT | undefined | null): OTDocSlot[] {
  if (!ot) return OT_DOC_SLOTS_ALL
  return OT_DOC_SLOTS_ALL.filter((slot) => {
    if (slot.required === "fumigation") return ot.fumigationAllowed
    return true
  })
}

export function otDocSlotsForStage(
  ot: OT | undefined | null,
  stage: OTStage,
): OTDocSlot[] {
  return otDocSlots(ot).filter((slot) => slot.stage === stage)
}

// Backend returns docs as a list of `ExporterOTDocuments` rows. UI almost
// always wants to look one up by slot — this is the index we render from.
export function indexDocsBySlot(
  docs: OTSlotDoc[] | null | undefined,
): Record<string, OTSlotDoc> {
  const out: Record<string, OTSlotDoc> = {}
  if (!docs) return out
  for (const doc of docs) {
    out[doc.slotId] = doc
  }
  return out
}

export function isSlotFilled(
  ot: OT | undefined | null,
  slotId: OTDocSlotId,
): boolean {
  if (!ot?.docs) return false
  return ot.docs.some((d) => d.slotId === slotId)
}

export function stageDocProgress(
  ot: OT | undefined | null,
  stage: OTStage,
): { filled: number; total: number } {
  const slots = otDocSlotsForStage(ot, stage).filter((s) => s.required !== false)
  if (!ot?.docs) return { filled: 0, total: slots.length }
  const filled = slots.filter((s) =>
    ot.docs!.some((d) => d.slotId === s.id),
  ).length
  return { filled, total: slots.length }
}

// Cross-stage progress used by the documents side panel and the list
// row's `n/m` chip — counts only doc slots, not the per-lot CCC.
export function totalDocProgress(
  ot: OT | undefined | null,
): { filled: number; total: number } {
  const slots = otDocSlots(ot).filter((s) => s.required !== false)
  if (!ot?.docs) return { filled: 0, total: slots.length }
  const filled = slots.filter((s) =>
    ot.docs!.some((d) => d.slotId === s.id),
  ).length
  return { filled, total: slots.length }
}
